import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string; // Optional for future use
}

/**
 * ProtectedRoute component that wraps protected routes.
 * Checks authentication via useAuth hook, redirects to login if not authenticated.
 * Saves intended route in query parameter for redirect after authentication.
 * Re-validates authentication on route changes to catch expired tokens.
 * Optionally checks user role against requiredRole prop (for future use).
 */
export const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const { isAuthenticated, loading, user, logout } = useAuth();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(false);

  // Re-validate authentication when route changes (catches expired tokens)
  // This ensures that even if user state exists in memory, we verify it's still valid
  // This fixes the issue where sidebar navigation (client-side) doesn't trigger
  // a full page reload, so expired tokens aren't detected until direct URL navigation
  // 
  // Note: This is a lightweight check that only validates on route change.
  // Most auth failures will be caught by API call 401 handlers, but this catches
  // the edge case where user navigates without making API calls.
  useEffect(() => {
    // Only validate if we think we're authenticated (to avoid unnecessary requests)
    // Skip validation if already loading or validating
    // Use a small delay to debounce rapid navigation
    if (!loading && isAuthenticated && !isValidating) {
      const timeoutId = setTimeout(() => {
        setIsValidating(true);
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
        
        fetch(`${API_URL}/users/me`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        })
          .then((response) => {
            if (response.status === 401) {
              // Token expired/invalid - clear auth state and let redirect happen
              logout();
            }
          })
          .catch(() => {
            // Network error - let the existing auth state handle it
          })
          .finally(() => {
            setIsValidating(false);
          });
      }, 100); // Small debounce to avoid excessive requests on rapid navigation

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [location.pathname, loading, isAuthenticated, isValidating, logout]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated, including intended route in query param
  if (!isAuthenticated) {
    // Save the current location as the intended route
    const from = location.pathname + location.search;
    return <Navigate to={`/auth/login?from=${encodeURIComponent(from)}`} replace />;
  }

  // Future: Check role if requiredRole is specified
  if (requiredRole && user?.role !== requiredRole) {
    // For MVP, all authenticated users are superuser, so this won't trigger
    // But structure is ready for future role checks
    const from = location.pathname + location.search;
    return <Navigate to={`/auth/login?from=${encodeURIComponent(from)}`} replace />;
  }

  return <>{children}</>;
};

